import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import { BOARD_CENTER } from './constants';
import { applyTempleFloor } from './templeFloor';

/** Sides of a moulding's cross-section. */
const RING_SIDES = 8;

/** Six columns, merged by material: the ornament does not cost a draw call per stone. */
export function createTemple(scene: Scene, parent: TransformNode | null, floor: Mesh) {
  const stone = new StandardMaterial('templeMarble', scene);
  stone.diffuseTexture = new Texture('/textures/temple/marble.png', scene);
  stone.specularColor = new Color3(0.25, 0.23, 0.19);
  stone.specularPower = 64;
  floor.receiveShadows = true;
  floor.position.z = BOARD_CENTER.z;
  applyTempleFloor(floor, stone);
  const gold = new StandardMaterial('templeBronze', scene);
  gold.diffuseColor = Color3.FromHexString('#aa8647');
  gold.specularColor = new Color3(0.6, 0.44, 0.18);
  const stones: Mesh[] = [];
  const trims: Mesh[] = [];
  const cylinder = (
    name: string,
    x: number,
    y: number,
    z: number,
    diameter: number,
    height: number,
    list = stones,
    top = diameter,
  ) => {
    const mesh = MeshBuilder.CreateCylinder(
      name,
      { diameterBottom: diameter, diameterTop: top, height, tessellation: 48 },
      scene,
    );
    mesh.position.set(x, y, z + BOARD_CENTER.z);
    list.push(mesh);
    return mesh;
  };
  /**
   * A round moulding: a small circle turned about the temple's axis. A torus turns its tube as
   * finely as its ring, 96 by 96, and five of them came to 92 000 triangles, more than all the
   * pieces on the board; eight sides are plenty for a tube a few centimetres thick.
   */
  const ring = (name: string, diameter: number, thickness: number, y: number) => {
    const shape = Array.from({ length: RING_SIDES + 1 }, (_, i) => {
      const a = (i / RING_SIDES) * Math.PI * 2;
      return new Vector3(
        diameter / 2 + (Math.cos(a) * thickness) / 2,
        (Math.sin(a) * thickness) / 2,
        0,
      );
    });
    const mesh = MeshBuilder.CreateLathe(
      name,
      { shape, tessellation: 96, cap: Mesh.NO_CAP },
      scene,
    );
    mesh.position.set(0, y, BOARD_CENTER.z);
    return mesh;
  };
  // Open-centred steps: opaque cylinder caps would mask the translucent floor from either side.
  for (const [name, radius, y, height] of [
    ['templeLowerStep', 4.15, -0.22, 0.18],
    ['templeUpperStep', 3.9, -0.08, 0.12],
  ] as const) {
    const inner = 3.78;
    const shape = [
      new Vector3(inner, -height / 2, 0),
      new Vector3(radius, -height / 2, 0),
      new Vector3(radius, height / 2, 0),
      new Vector3(inner, height / 2, 0),
      new Vector3(inner, -height / 2, 0),
    ];
    const step = MeshBuilder.CreateLathe(
      name,
      { shape, tessellation: 96, cap: Mesh.NO_CAP },
      scene,
    );
    step.position.set(0, y, BOARD_CENTER.z);
    stones.push(step);
  }
  for (let i = 0; i < 6; i++) {
    const angle = (i * Math.PI) / 3;
    const x = 3.3 * Math.cos(angle),
      z = 3.3 * Math.sin(angle);
    cylinder(`pillar${i}Base`, x, 0.12, z, 0.72, 0.24);
    cylinder(`pillar${i}Foot`, x, 0.3, z, 0.57, 0.12);
    cylinder(`pillar${i}Shaft`, x, 2.15, z, 0.44, 3.6, stones, 0.36);
    // Slender inset ribs suggest fluting without expensive sculpted meshes.
    for (let rib = 0; rib < 12; rib++) {
      const a = (rib * Math.PI) / 6;
      cylinder('flute', x + Math.cos(a) * 0.192, 2.15, z + Math.sin(a) * 0.192, 0.035, 3.45);
    }
    cylinder(`pillar${i}Neck`, x, 3.98, z, 0.48, 0.12, trims);
    cylinder(`pillar${i}Capital`, x, 4.12, z, 0.72, 0.2);
  }
  cylinder('templeRoof', 0, 4.38, 0, 7.8, 0.32);
  for (const y of [4.22, 4.52]) trims.push(ring('roofCornice', 7.65, 0.09, y));
  // Concentric dark/gold floor inlays retain a clear, uncluttered playing area.
  for (const diameter of [3.0, 6.1, 7.35]) trims.push(ring('floorInlay', diameter, 0.018, 0.007));
  const structure = Mesh.MergeMeshes(stones, true, true)!;
  structure.name = 'sixPillarTemple';
  structure.material = stone;
  const ornament = Mesh.MergeMeshes(trims, true, true)!;
  ornament.name = 'templeInlays';
  ornament.material = gold;
  for (const mesh of [structure, ornament]) {
    mesh.parent = parent;
    mesh.isPickable = false;
    mesh.receiveShadows = true;
  }
  const ceiling = MeshBuilder.CreateDisc(
    'fantasyGodsCeiling',
    { radius: 3.64, tessellation: 96, sideOrientation: Mesh.DOUBLESIDE },
    scene,
  );
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.set(0, 4.205, BOARD_CENTER.z);
  ceiling.parent = parent;
  ceiling.isPickable = false;
  const fresco = new StandardMaterial('fantasyFresco', scene);
  fresco.diffuseTexture = new Texture('/textures/temple/fresco.png', scene);
  fresco.emissiveColor = new Color3(0.22, 0.2, 0.17);
  fresco.specularColor = Color3.Black();
  ceiling.material = fresco;
  return { structure, ornament, center: new Vector3(0, 0, BOARD_CENTER.z) };
}
